import { JSONSerializable } from '@highimpact-frontend/api-types';
import {
  array as A,
  filterJSONSerializableByPaths,
  JSONSerializable as JSS,
  record as R,
} from '@highimpact/data-utils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { upgradeAxisPreferences } from '../config/plotPreferences';
import {
  AxisKey,
  AxisPreferences,
  CurveId,
  CurvePreferences,
  PlotPreferences,
  SourceSlotIndex,
  TimeseriesAxisLabelMode,
} from '../config/plotTypes';
import { averageColorsInHSLA } from '../config/relationColors';
import { PlotDataInput } from './PlotDataInput';
import {
  buildTimeseriesAxisTicks,
  mapInstantsToEpochMs,
  resolveTimeseriesAxisLabelMode,
  TIMESERIES_TIMEZONE_LABEL_BOTTOM_MARGIN,
  TIMESERIES_XAXIS_HOVERFORMAT,
  type EpochMillisecondRange,
} from './timeseriesAxisTicks';
import {
  applyCurveSessionVisibilityToTraces,
  CurveSessionVisibility,
} from './curveSessionVisibility';
import {
  enrichTimeseriesTracesWithUnifiedHoverLabels,
  normalizePointCustomdata,
  normalizeTimeseriesAdditionalTracesX,
  shiftHovertemplateCustomdataIndices,
} from './timeseriesHoverLabels';
import { centermostPositions, positionFromIndex } from './yAxisPositioning';

// -- types

type PlotlyDataWithMeta = Partial<Plotly.PlotData> & {
  meta?: { curveId?: string; rowIndices?: number[] };
  y?: Array<number | null>;
  customdata?: unknown;
  hovertemplate?: string;
  name?: string;
};

export interface PlotlySensorTracePatch {
  marker?: {
    line?: {
      color?: (string | undefined)[];
      width?: (number | undefined)[];
    };
  };
  customdataAppend?: unknown[][];
  hovertemplateAugment?: string;
}

/**
 * Options for building the plotly data.
 */
export interface BuildPlotlyDataOptions {
  /** Additional traces to add to the plot; such as threshold traces. */
  additionalTraces?: Partial<Plotly.PlotData>[];
  /** Sensor trace patches by curve id; contains info such as threshold breaches. */
  sensorTracePatchesByCurveId?: Record<string, PlotlySensorTracePatch>;
  /** IANA timezone used to render timeseries x-axis labels. */
  displayTimezone?: string;
  /** Timeseries hover/tick suffix mode. */
  timeseriesAxisLabelMode?: TimeseriesAxisLabelMode;
  /** BCP 47 locale tag(s) for tick/hover labels. */
  locale?: string | string[];
  /** Visible x-axis range in epoch ms; drives hover label granularity. */
  visibleXRangeMs?: EpochMillisecondRange;
  /** Session legend visibility overrides keyed by curve id. */
  curveSessionVisibility?: CurveSessionVisibility;
}

/**
 * Options for building the plotly layout.
 */
export interface BuildPlotlyLayoutOptions {
  /** IANA timezone used to render timeseries x-axis labels. */
  displayTimezone?: string;
  /** Timeseries tick suffix mode. */
  timeseriesAxisLabelMode?: TimeseriesAxisLabelMode;
  /** Visible x-axis range in epoch ms used to seed manual ticks. */
  visibleXRangeMs?: EpochMillisecondRange;
  /** BCP 47 locale tag(s) for tick labels. */
  locale?: string | string[];
}

function applyThresholdLineShapesToAdditionalTraces(
  additionalTraces: Partial<Plotly.PlotData>[] | undefined,
  prefs: PlotPreferences
): Partial<Plotly.PlotData>[] {
  const thresholdLineShapeByAssignmentId =
    prefs.thresholdPrefs?.visualization?.lineShapeByThresholdAssignmentId;

  if (!additionalTraces || !thresholdLineShapeByAssignmentId) {
    return additionalTraces ?? [];
  }

  return additionalTraces.map((trace) => {
    const traceMeta = (trace as { meta?: unknown }).meta;
    const thresholdScopeAssignmentId =
      typeof traceMeta === 'object' && traceMeta !== null
        ? (traceMeta as { thresholdScopeAssignmentId?: unknown })
            .thresholdScopeAssignmentId
        : undefined;

    if (typeof thresholdScopeAssignmentId !== 'number') {
      return trace;
    }

    const shape = thresholdLineShapeByAssignmentId[thresholdScopeAssignmentId];

    if (!shape) {
      return trace;
    }

    return {
      ...trace,
      line: {
        ...(trace.line ?? {}),
        shape,
      },
    };
  });
}

// -- utils

dayjs.extend(utc);
dayjs.extend(timezone);

export function getPlotlyXShortId(): string {
  return 'x';
}

export function getPlotlyXLayoutId(): string {
  return 'xaxis';
}

export function getPlotlyYShortId(
  prefs: PlotPreferences,
  axisKey: AxisKey
): string {
  const idx = prefs.yAxisOrder.indexOf(axisKey);
  if (idx === -1) return 'y';

  const n = idx + 1;
  return n === 1 ? 'y' : `y${n}`;
}

export function getPlotlyYLayoutId(
  prefs: PlotPreferences,
  axisKey: AxisKey
): string {
  const idx = prefs.yAxisOrder.indexOf(axisKey);
  if (idx === -1) return 'yaxis';

  const n = idx + 1;
  return n === 1 ? 'yaxis' : `yaxis${n}`;
}

export function mapDashStyle(style: CurvePreferences['lineStyle']) {
  switch (style) {
    case 'dashed':
      return 'dash';
    case 'dotted':
      return 'dot';
    case 'solid':
      return 'solid';
    case undefined:
      return undefined;
  }
}

export function findXAxisKey(prefs: PlotPreferences): AxisKey | undefined {
  return R.keys(prefs.axisPrefs).find((key) => prefs.axisPrefs[key]?.isXAxis);
}

export function findXCurveId(
  prefs: PlotPreferences,
  xAxisKey: AxisKey
): CurveId | undefined {
  return Object.entries(prefs.dataSourceToAxis).find(
    ([, axis]) => axis === xAxisKey
  )?.[0];
}

/**
 * Resolves the scatter X curve, including fallbacks for stale persisted prefs
 * where `isXAxis` and `dataSourceToAxis` may be out of sync.
 */
export function findScatterXCurveId(
  prefs: PlotPreferences
): CurveId | undefined {
  const xAxisKey = findXAxisKey(prefs);
  if (xAxisKey) {
    const fromXAxisKey = findXCurveId(prefs, xAxisKey);
    if (fromXAxisKey) {
      return fromXAxisKey;
    }
  }

  for (const [curveId, axisKey] of Object.entries(prefs.dataSourceToAxis)) {
    if (prefs.axisPrefs[axisKey]?.isXAxis) {
      return curveId;
    }
  }

  const sourceEntries = Object.entries(prefs.curveToSourceIndex ?? {});
  if (sourceEntries.length > 0) {
    return sourceEntries.reduce((max, current) =>
      current[1] > max[1] ? current : max
    )[0];
  }

  const order =
    prefs.curveOrder ?? (Object.keys(prefs.curvePrefs) as CurveId[]);
  return order[order.length - 1];
}

/** Resolves the scatter X axis key from prefs or the X curve mapping. */
export function resolveScatterXAxisKey(
  prefs: PlotPreferences,
  xCurveId: CurveId
): AxisKey | undefined {
  return findXAxisKey(prefs) ?? prefs.dataSourceToAxis[xCurveId];
}

function getDataValueForCurve(
  prefs: PlotPreferences,
  row: Record<string, number> | undefined,
  curveId: CurveId
): number | null {
  if (!row) {
    return null;
  }

  const sourceSlotIndex: SourceSlotIndex | undefined =
    prefs.curveToSourceIndex?.[curveId];

  if (sourceSlotIndex !== undefined) {
    return row[`${sourceSlotIndex}`] ?? null;
  }

  return row[curveId] ?? null;
}

export function getAxisKeyToColor(
  pref: PlotPreferences
): Record<AxisKey, string> {
  const axisToDataSource = R.revert(pref.dataSourceToAxis);
  const axisToColor = R.map((sources) => {
    const colors = sources
      .map((k) => pref.curvePrefs[k].color)
      .filter((c) => c !== undefined);
    const allEqual = A.mapEqual(colors, (a) => a);
    return allEqual ? colors[0] ?? 'black' : 'black';
  }, axisToDataSource);
  return axisToColor;
}

export function getPlotlyLayoutIdToAxisKey(
  prefs: PlotPreferences,
  type: 'timeseries' | 'scatter'
): Record<string, AxisKey> {
  const acc: Record<string, AxisKey> = {};
  R.keys(prefs.axisPrefs).forEach((axisKey) => {
    const plotlyLayoutId = getPlotlyYLayoutId(prefs, axisKey);
    acc[plotlyLayoutId] = axisKey;
  });
  if (type === 'scatter') {
    const plotlyXLayoutId = 'xaxis';
    const axisKey = findXAxisKey(prefs);
    if (axisKey) acc[plotlyXLayoutId] = axisKey;
  }
  return acc;
}

/**
 * Converts a local-time timestamp to an ISO string, trying multiple formats for parsing.
 *
 * @param localTimeString - the local time string to be converted
 * @param formats - the array of formats to try for parsing the local time string
 * @returns the ISO string if parsing is successful, or an empty string if all parsing attempts fail
 */
function parseAndConvert(
  localTimeString: string,
  formats: readonly string[]
): string {
  const parsed = dayjs(localTimeString, formats as string[], true);

  if (!parsed.isValid()) {
    return '';
  }

  return parsed.utc().toISOString();
}

/**
 * Takes a local time string in one of several formats and converts it to an ISO string in UTC.
 *
 * Handles both strings with millisecond precision and simple dates
 * that only contain year, month, and day.
 *
 * @param localTimeString - the local time string to be converted
 * @returns the ISO string in UTC ('YYYY-MM-DDTHH:mm:ss.SSSZ')
 * @throws Error if the input string does not match any expected format
 */
export function convertPlotlyFormatToIso(localTimeString: string): string {
  const allowedFormats = [
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
  ] as const;

  const result = parseAndConvert(localTimeString, allowedFormats);

  if (!result) {
    throw new Error(
      `unable to parse the provided date string: ${localTimeString}`
    );
  }

  return result;
}

function toFiniteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getTraceNumericY(
  trace: PlotlyDataWithMeta | undefined
): (number | null)[] {
  if (!trace || !Array.isArray(trace.y)) {
    return [];
  }

  return trace.y.map((value: number | null) => toFiniteNumberOrNull(value));
}

function buildTraceByCurveId(
  traces: PlotlyDataWithMeta[],
  prefs: PlotPreferences
): Map<CurveId, PlotlyDataWithMeta> {
  const traceByCurveId = new Map<CurveId, PlotlyDataWithMeta>();

  traces.forEach((trace) => {
    const curveIdFromMeta =
      typeof trace.meta === 'object' && trace.meta !== null
        ? (trace.meta as { curveId?: string }).curveId ?? undefined
        : undefined;

    if (curveIdFromMeta && prefs.curvePrefs[curveIdFromMeta]) {
      traceByCurveId.set(curveIdFromMeta, trace);
      return;
    }

    const fallbackCurveId = Object.keys(prefs.curvePrefs).find(
      (curveId) =>
        (prefs.curvePrefs[curveId]?.displayName ?? curveId) === trace.name
    ) as CurveId | undefined;

    if (fallbackCurveId) {
      traceByCurveId.set(fallbackCurveId, trace);
    }
  });

  return traceByCurveId;
}

function computeRollingCorrelation(
  primarySeries: (number | null)[],
  secondarySeries: (number | null)[],
  window = 15
): (number | null)[] {
  return primarySeries.map((_, index) => {
    if (index + 1 < window) {
      return null;
    }

    const start = index - window + 1;
    const primarySlice = primarySeries.slice(start, index + 1);
    const secondarySlice = secondarySeries.slice(start, index + 1);

    if (
      primarySlice.some((value) => value === null) ||
      secondarySlice.some((value) => value === null)
    ) {
      return null;
    }

    const pValues = primarySlice as number[];
    const sValues = secondarySlice as number[];

    const meanPrimary = pValues.reduce((acc, value) => acc + value, 0) / window;
    const meanSecondary =
      sValues.reduce((acc, value) => acc + value, 0) / window;

    let numerator = 0;
    let denominatorPrimary = 0;
    let denominatorSecondary = 0;

    for (let i = 0; i < window; i++) {
      const diffPrimary = pValues[i] - meanPrimary;
      const diffSecondary = sValues[i] - meanSecondary;

      numerator += diffPrimary * diffSecondary;
      denominatorPrimary += diffPrimary ** 2;
      denominatorSecondary += diffSecondary ** 2;
    }

    const denominator = Math.sqrt(denominatorPrimary * denominatorSecondary);

    if (denominator === 0 || !Number.isFinite(denominator)) {
      return null;
    }

    return numerator / denominator;
  });
}

function enrichTimeseriesHover(
  traces: PlotlyDataWithMeta[],
  prefs: PlotPreferences
): PlotlyDataWithMeta[] {
  const curveNameTemplate = '<b>%{fullData.name}</b>';

  traces.forEach((trace) => {
    trace.hovertemplate = `${curveNameTemplate}: %{y:.2f}<extra></extra>`;
  });

  if (!prefs.curveRelations || prefs.curveRelations.length === 0) {
    return traces;
  }

  const traceByCurveId = buildTraceByCurveId(traces, prefs);

  for (const rel of prefs.curveRelations) {
    if (rel.relationType === 'difference') {
      const referenceKey = rel.mainCurve;
      const currentKey = rel.relatedCurves[0];

      const referenceTrace = traceByCurveId.get(referenceKey);
      const currentTrace = currentKey
        ? traceByCurveId.get(currentKey)
        : undefined;

      if (referenceTrace) {
        referenceTrace.hovertemplate = `${curveNameTemplate} (reference): %{y:.2f}<extra></extra>`;
      }

      if (currentTrace && referenceTrace) {
        const currentSeries = getTraceNumericY(currentTrace);
        const referenceSeries = getTraceNumericY(referenceTrace);

        currentTrace.customdata = currentSeries.map((value, index) => {
          const referenceValue = referenceSeries[index] ?? null;

          if (value === null || referenceValue === null) {
            return null;
          }

          return value - referenceValue;
        });

        currentTrace.hovertemplate = `${curveNameTemplate}: %{y:.2f}<br>ecart: %{customdata:+.2f}<extra></extra>`;
      }
    }

    if (rel.relationType === 'correlation') {
      const primaryKey = rel.mainCurve;
      const secondaryKey = rel.relatedCurves[0];

      const primaryTrace = traceByCurveId.get(primaryKey);
      const secondaryTrace = secondaryKey
        ? traceByCurveId.get(secondaryKey)
        : undefined;

      if (secondaryTrace) {
        secondaryTrace.hovertemplate = `${curveNameTemplate} (corr): %{y:.2f}<extra></extra>`;
      }

      if (primaryTrace && secondaryTrace) {
        const primarySeries = getTraceNumericY(primaryTrace);
        const secondarySeries = getTraceNumericY(secondaryTrace);
        const rollingCorrelation = computeRollingCorrelation(
          primarySeries,
          secondarySeries
        );

        primaryTrace.customdata = rollingCorrelation.map((value) => [value]);
        primaryTrace.hovertemplate = `${curveNameTemplate}: %{y:.2f}<br>correlation locale: %{customdata[0]:.3f}<extra></extra>`;
      }
    }

    if (rel.relationType === 'interval') {
      const [minKey, maxKey] = rel.relatedCurves;
      const mainKey = rel.mainCurve;

      const minTrace = minKey ? traceByCurveId.get(minKey) : undefined;
      const maxTrace = maxKey ? traceByCurveId.get(maxKey) : undefined;
      const mainTrace = traceByCurveId.get(mainKey);

      if (minTrace) {
        minTrace.hovertemplate = `${curveNameTemplate} (min): %{y:.2f}<extra></extra>`;
      }

      if (maxTrace) {
        maxTrace.hovertemplate = `${curveNameTemplate} (max): %{y:.2f}<extra></extra>`;
      }

      if (mainTrace && minTrace && maxTrace) {
        const mainSeries = getTraceNumericY(mainTrace);
        const minSeries = getTraceNumericY(minTrace);
        const maxSeries = getTraceNumericY(maxTrace);

        mainTrace.customdata = mainSeries.map((mainValue, index) => {
          const minValue = minSeries[index] ?? null;
          const maxValue = maxSeries[index] ?? null;

          if (mainValue === null || minValue === null || maxValue === null) {
            return [null, null, 'n/a'];
          }

          const spread = maxValue - minValue;
          const position =
            spread !== 0 ? ((mainValue - minValue) / spread) * 100 : 0;
          const outOfRange = mainValue < minValue || mainValue > maxValue;

          return [spread, position, outOfRange ? 'oui' : 'non'];
        });

        mainTrace.hovertemplate = `${curveNameTemplate}: %{y:.2f}<br>amplitude: %{customdata[0]:.2f}<br>position: %{customdata[1]:.1f}%<br>hors intervalle: %{customdata[2]}<extra></extra>`;
      }
    }
  }

  return traces;
}

function buildScatterHoverTemplate(xLabel: string): string {
  return `<b>${xLabel}</b>: %{x:.2f}<br><b>%{fullData.name}</b>: %{y:.2f}<extra></extra>`;
}

function enrichScatterHover(
  traces: PlotlyDataWithMeta[],
  prefs: PlotPreferences,
  scatterXCurveId: CurveId,
  scatterXAxisKey: AxisKey
): PlotlyDataWithMeta[] {
  const xLabel =
    prefs.curvePrefs[scatterXCurveId]?.displayName ||
    prefs.axisPrefs[scatterXAxisKey]?.displayName ||
    scatterXCurveId;

  const hovertemplate = buildScatterHoverTemplate(xLabel);
  traces.forEach((trace) => {
    trace.hovertemplate = hovertemplate;
  });
  return traces;
}

function applyCurveRelations(
  traces: PlotlyDataWithMeta[],
  prefs: PlotPreferences,
  type: 'timeseries' | 'scatter'
): PlotlyDataWithMeta[] {
  if (!prefs.curveRelations || prefs.curveRelations.length === 0) {
    return traces;
  }

  const result: PlotlyDataWithMeta[] = [];
  const usedKeys = new Set<string>();
  const traceByCurveId = new Map<CurveId, PlotlyDataWithMeta>();

  traces.forEach((trace) => {
    const curveIdFromMeta =
      typeof trace.meta === 'object' && trace.meta !== null
        ? (trace.meta as { curveId?: string }).curveId ?? undefined
        : undefined;

    if (curveIdFromMeta && prefs.curvePrefs[curveIdFromMeta]) {
      traceByCurveId.set(curveIdFromMeta, trace);
      return;
    }

    const fallbackCurveId = Object.keys(prefs.curvePrefs).find(
      (curveId) =>
        (prefs.curvePrefs[curveId]?.displayName ?? curveId) === trace.name
    ) as CurveId | undefined;

    if (fallbackCurveId) {
      traceByCurveId.set(fallbackCurveId, trace);
    }
  });

  for (const rel of prefs.curveRelations) {
    const group = rel.displayName || rel.mainCurve;

    if (rel.relationType === 'interval' && type === 'timeseries') {
      const [minKey, maxKey] = rel.relatedCurves;
      const mainKey = rel.mainCurve;

      const minTrace = minKey ? traceByCurveId.get(minKey) : undefined;
      const maxTrace = maxKey ? traceByCurveId.get(maxKey) : undefined;
      const mainTrace = mainKey ? traceByCurveId.get(mainKey) : undefined;

      if (minTrace) {
        result.push({ ...minTrace, legendgroup: group });
        usedKeys.add(minKey);
      }
      if (maxTrace) {
        const canFillInterval = Boolean(minTrace);
        result.push({
          ...maxTrace,
          fill: canFillInterval ? 'tonexty' : undefined,
          fillcolor: canFillInterval ? rel.color : undefined,
          legendgroup: group,
        });
        usedKeys.add(maxKey);
      }
      if (mainTrace) {
        result.push({ ...mainTrace, legendgroup: group });
        usedKeys.add(mainKey);
      }
    }

    if (
      (rel.relationType === 'correlation' ||
        rel.relationType === 'difference') &&
      type === 'timeseries'
    ) {
      const firstKey = rel.mainCurve;
      const secondKey = rel.relatedCurves[0];

      const firstTrace = firstKey ? traceByCurveId.get(firstKey) : undefined;
      const secondTrace = secondKey ? traceByCurveId.get(secondKey) : undefined;
      const autoFillColor = averageColorsInHSLA(
        [
          prefs.curvePrefs[firstKey]?.color,
          secondKey ? prefs.curvePrefs[secondKey]?.color : undefined,
        ].filter((color): color is string => Boolean(color)),
        0.35
      );

      if (firstTrace) {
        result.push({ ...firstTrace, legendgroup: group });
        usedKeys.add(firstKey);
      }

      if (secondTrace) {
        const canFillPairRelation = Boolean(firstTrace);
        result.push({
          ...secondTrace,
          fill: canFillPairRelation ? 'tonexty' : undefined,
          fillcolor: canFillPairRelation
            ? rel.color || autoFillColor
            : undefined,
          legendgroup: group,
        });
        usedKeys.add(secondKey);
      }

      continue;
    }

    if (
      rel.relationType === 'correlation' ||
      rel.relationType === 'difference'
    ) {
      const allKeys = [rel.mainCurve, rel.relatedCurves[0]].filter(
        (key): key is CurveId => Boolean(key)
      );

      for (const key of allKeys) {
        const trace = traces.find(
          (t) => t.name === (prefs.curvePrefs[key]?.displayName ?? key)
        );

        if (trace) {
          result.push({ ...trace, legendgroup: group });
          usedKeys.add(key);
        }
      }
    }
  }

  const orderedCurveKeys = [
    ...(prefs.curveOrder ?? []).filter((k) => prefs.curvePrefs[k]),
    ...Object.keys(prefs.curvePrefs).filter(
      (k) => !(prefs.curveOrder ?? []).includes(k)
    ),
  ];

  for (const t of traces) {
    const dsKey = orderedCurveKeys.find(
      (k) => (prefs.curvePrefs[k].displayName ?? k) === t.name
    );
    if (!dsKey || !usedKeys.has(dsKey)) {
      result.push(t);
    }
  }

  return result;
}

/**
 * Build margins that are adaptive to the plot's content.
 */
export function buildPlotlyMargins(
  prefs: PlotPreferences,
  mode: 'timeseries' | 'scatter'
): Partial<Plotly.Margin> {
  const xAxisKey = findXAxisKey(prefs);
  const xAxisTitle = xAxisKey ? prefs.axisPrefs[xAxisKey].displayName : undefined;

  const axisTitleBottomMargin = xAxisTitle ? 20 : 0;
  const showRangeSlider = mode === 'timeseries' && prefs.showRangeSlider;
  const baseBottomMargin = showRangeSlider ? 20 : 45;
  const timezoneLabelBottomMargin =
    mode === 'timeseries' && !showRangeSlider
      ? TIMESERIES_TIMEZONE_LABEL_BOTTOM_MARGIN
      : 0;
  const finalBottomMargin =
    baseBottomMargin + axisTitleBottomMargin + timezoneLabelBottomMargin;

  return {
    t: 0,
    r: 50,
    b: finalBottomMargin,
    l: 50,
  };
}

// -- timeseries linear axis (skeleton) --

/**
 * Maps sorted UTC instants to epoch-ms x values for timeseries traces.
 *
 * @param dateKeys - Sorted measurement instants from plot data.
 * @returns Epoch milliseconds in the same order.
 */
export function mapTimeseriesTraceXValues(
  dateKeys: readonly Date[]
): readonly number[] {
  return mapInstantsToEpochMs(dateKeys);
}

/**
 * Builds manual tick payload for the timeseries linear x-axis.
 *
 * @param layoutOptions - Display timezone, label mode, and visible range.
 * @returns Plotly tick payload, or `undefined` when range is unavailable.
 */
export function buildTimeseriesLayoutTicks(
  layoutOptions?: BuildPlotlyLayoutOptions
): { tickvals: readonly number[]; ticktext: readonly string[] } | undefined {
  const visibleRangeMs = layoutOptions?.visibleXRangeMs;
  const displayTimezone = layoutOptions?.displayTimezone;

  if (!visibleRangeMs || !displayTimezone) {
    return undefined;
  }

  return buildTimeseriesAxisTicks({
    visibleRangeMs,
    displayTimezone,
    labelMode: layoutOptions.timeseriesAxisLabelMode,
    locale: layoutOptions.locale,
  });
}

// -- exported functions --

export function buildPlotlyData(
  prefs: PlotPreferences,
  data: PlotDataInput,
  type: 'timeseries' | 'scatter',
  useWebGL = true,
  options?: BuildPlotlyDataOptions
): Plotly.Data[] {
  const traces: PlotlyDataWithMeta[] = [];
  const hasTimeSeriesRelation =
    type === 'timeseries' && (prefs.curveRelations?.length ?? 0) > 0;
  const traceType: Plotly.Data['type'] =
    useWebGL && !hasTimeSeriesRelation ? 'scattergl' : 'scatter';
  const orderedCurveKeys = [
    ...(prefs.curveOrder ?? []).filter((k) => prefs.curvePrefs[k]),
    ...R.keys(prefs.curvePrefs).filter(
      (k) => !(prefs.curveOrder ?? []).includes(k)
    ),
  ];

  const dateKeys = [...data.keys()].sort((a, b) => a.getTime() - b.getTime());
  const xShortId = getPlotlyXShortId();
  const scatterXCurveId = findScatterXCurveId(prefs);
  const scatterXAxisKey = scatterXCurveId
    ? resolveScatterXAxisKey(prefs, scatterXCurveId)
    : undefined;

  // -------------------------
  // TIMESERIES MODE
  // -------------------------
  if (type === 'timeseries') {
    for (const dsKey of orderedCurveKeys) {
      const curve = prefs.curvePrefs[dsKey];
      if (curve.visible === false) continue;

      const axisKey = prefs.dataSourceToAxis[dsKey];
      const yShortId = getPlotlyYShortId(prefs, axisKey);

      // Linear x-axis: epoch ms coordinates; labels come from layout ticktext.
      const x = mapTimeseriesTraceXValues(dateKeys);
      const y = dateKeys.map((d) =>
        getDataValueForCurve(prefs, data.get(d), dsKey)
      );

      const nonNullYIndices = y
        .map((v, i) => (v !== null && v !== undefined ? i : undefined))
        .filter((v): v is number => v !== undefined);
      const xFiltered = x.filter((_, i) => nonNullYIndices.includes(i));
      const yFiltered = y.filter((_, i) => nonNullYIndices.includes(i));

      traces.push({
        x: xFiltered,
        y: yFiltered,
        name: curve.displayName || dsKey,
        // rowIndices maps a trace point index to the corresponding measured row index.
        meta: { curveId: dsKey, rowIndices: nonNullYIndices },
        type: traceType,
        mode: 'lines',
        line: {
          color: curve.color ?? undefined,
          width: curve.thicknessPx ?? 2,
          dash: mapDashStyle(curve.lineStyle),
          shape: curve.shape ?? 'linear',
          smoothing: curve.shape === 'spline' ? 0.5 : undefined,
        },
        xaxis: xShortId,
        yaxis: yShortId,
      });
    }

    const baseTraces = applyCurveRelations(
      enrichTimeseriesHover(traces, prefs),
      prefs,
      'timeseries'
    );
    const sessionVisibleTraces = applyCurveSessionVisibilityToTraces(
      baseTraces,
      options?.curveSessionVisibility
    );
    const hoverEnrichedTraces = enrichTimeseriesTracesWithUnifiedHoverLabels(
      sessionVisibleTraces,
      options?.displayTimezone,
      resolveTimeseriesAxisLabelMode(options?.timeseriesAxisLabelMode),
      options?.visibleXRangeMs,
      options?.locale
    );
    const patchedTraces = applySensorTracePatches(
      hoverEnrichedTraces,
      options?.sensorTracePatchesByCurveId
    );
    const normalizedAdditionalTraces = normalizeTimeseriesAdditionalTracesX(
      options?.additionalTraces ?? []
    );

    return [
      ...patchedTraces,
      ...applyThresholdLineShapesToAdditionalTraces(
        normalizedAdditionalTraces,
        prefs
      ),
    ];
  }

  // -------------------------
  // SCATTER MODE
  // -------------------------
  if (!scatterXCurveId || !scatterXAxisKey) return [];

  const xValues = dateKeys.map((d) =>
    getDataValueForCurve(prefs, data.get(d), scatterXCurveId)
  );

  for (const dsKey of orderedCurveKeys) {
    if (dsKey === scatterXCurveId) continue;

    const curve = prefs.curvePrefs[dsKey];
    if (curve.visible === false) continue;

    const axisKey = prefs.dataSourceToAxis[dsKey];
    const yShortId = getPlotlyYShortId(prefs, axisKey);

    const yValues = dateKeys.map((d) =>
      getDataValueForCurve(prefs, data.get(d), dsKey)
    );

    traces.push({
      x: xValues,
      y: yValues,
      name: curve.displayName || dsKey,
      meta: { curveId: dsKey },
      type: traceType,
      mode: 'markers',
      marker: {
        size: curve.thicknessPx ?? 10,
        color: curve.color ?? undefined,
      },
      xaxis: xShortId,
      yaxis: yShortId,
    });
  }

  enrichScatterHover(traces, prefs, scatterXCurveId, scatterXAxisKey);
  const baseTraces = applyCurveRelations(traces, prefs, 'scatter');
  const patchedTraces = applySensorTracePatches(
    baseTraces,
    options?.sensorTracePatchesByCurveId
  );

  return [
    ...patchedTraces,
    ...applyThresholdLineShapesToAdditionalTraces(
      options?.additionalTraces,
      prefs
    ),
  ];
}

function applySensorTracePatches(
  traces: PlotlyDataWithMeta[],
  sensorTracePatchesByCurveId:
    | Record<string, PlotlySensorTracePatch>
    | undefined
): PlotlyDataWithMeta[] {
  if (!sensorTracePatchesByCurveId) {
    return traces;
  }

  // @ts-expect-error - incomplete Plotly types don't allow some of the properties we need to patch
  return traces.map((trace) => {
    const traceMeta = trace.meta as
      | { curveId?: string; rowIndices?: number[] }
      | undefined;
    const curveId = traceMeta?.curveId;

    if (!curveId) {
      return trace;
    }

    const patch = sensorTracePatchesByCurveId[curveId];

    if (!patch) {
      return trace;
    }

    const rowIndices = traceMeta?.rowIndices;
    const existingCustomData = Array.isArray(trace.customdata)
      ? trace.customdata
      : [];

    const mappedColor = rowIndices
      ? rowIndices.map((rowIndex) => patch.marker?.line?.color?.[rowIndex])
      : patch.marker?.line?.color;
    const mappedWidth = rowIndices
      ? rowIndices.map((rowIndex) => patch.marker?.line?.width?.[rowIndex])
      : patch.marker?.line?.width;
    const mappedAppend = rowIndices
      ? rowIndices.map((rowIndex) => patch.customdataAppend?.[rowIndex] ?? [])
      : patch.customdataAppend;

    const maxLength = Math.max(
      existingCustomData.length,
      mappedAppend?.length ?? 0,
      0
    );
    const mergedCustomData = Array.from({ length: maxLength }, (_, index) => {
      const existing = existingCustomData[index];
      const existingArray = normalizePointCustomdata(existing);
      const append = mappedAppend?.[index] ?? [];

      return [...existingArray, ...append];
    });

    const thresholdAugmentOffset = (() => {
      for (let index = 0; index < maxLength; index += 1) {
        const append = mappedAppend?.[index] ?? [];
        if (append.length > 0) {
          return normalizePointCustomdata(existingCustomData[index]).length;
        }
      }

      return normalizePointCustomdata(existingCustomData[0]).length;
    })();

    const marker = (trace.marker as Record<string, unknown> | undefined) ?? {};
    const markerLine =
      (marker.line as Record<string, unknown> | undefined) ?? {};
    const hovertemplateAugment = patch.hovertemplateAugment
      ? shiftHovertemplateCustomdataIndices(
          patch.hovertemplateAugment,
          thresholdAugmentOffset
        )
      : undefined;
    const hovertemplate = hovertemplateAugment
      ? `${trace.hovertemplate ?? '%{y}<extra></extra>'}${hovertemplateAugment}`
      : trace.hovertemplate;

    return {
      ...trace,
      marker: {
        ...marker,
        ...(patch.marker ?? {}),
        line: {
          ...markerLine,
          ...(patch.marker?.line ?? {}),
          color: mappedColor,
          width: mappedWidth,
        },
      },
      customdata: mergedCustomData,
      hovertemplate,
    };
  });
}

/** Whitelisted session layout paths merged after prefs-driven layout build. */
export const DEFAULT_PLOT_LAYOUT_STATE_WHITELIST = [
  'xaxis.range',
  'yaxis.range',
  'yaxis*.range',
] as const;

export type PlotLayoutStateWhitelist = readonly string[];

/**
 * Merge the session layout state into the prefs-driven layout.
 * 
 * Will only merge paths that are whitelisted
 * (or the default paths if no whitelist is provided).
 */
function mergeFilteredLayoutState(
  layout: Partial<Plotly.Layout>,
  state: Partial<Plotly.Layout>,
  whitelist: PlotLayoutStateWhitelist = DEFAULT_PLOT_LAYOUT_STATE_WHITELIST
): Partial<Plotly.Layout> {
  const filteredState = filterJSONSerializableByPaths(
    state as JSONSerializable,
    whitelist
  );

  return JSS.deepMerge(
    layout as Record<string, JSONSerializable>,
    filteredState as Record<string, JSONSerializable>
  ) as Partial<Plotly.Layout>;
}

/**
 * Resolve prefs-driven Plotly axis fields (no session state).
 */
export function resolveAxisPlotlyLayoutFields(
  axis: AxisPreferences
): { range?: [number, number]; fixedrange: boolean } {
  const { useBounds, zoomEnabled, min, max } = upgradeAxisPreferences(axis);
  const fixedrange = zoomEnabled === false;

  if (useBounds && min !== undefined && max !== undefined) {
    return { range: [min, max], fixedrange };
  }

  return { range: undefined, fixedrange };
}

/**
 * Build the Plotly layout from the plot preferences and the layout state.
 *
 * @param prefs - The plot preferences.
 * @param mode - The plot mode ('timeseries' or 'scatter')
 * @param state - The layout state; whitelisted keys are merged at the end.
 * @param layoutStateWhitelist - Allowed session paths; defaults to axis ranges only.
 * @returns The Plotly layout.
 */
export function buildPlotlyLayout(
  prefs: PlotPreferences,
  mode: 'timeseries' | 'scatter',
  state?: Partial<Plotly.Layout>,
  layoutStateWhitelist: PlotLayoutStateWhitelist = DEFAULT_PLOT_LAYOUT_STATE_WHITELIST,
  layoutOptions?: BuildPlotlyLayoutOptions
): Partial<Plotly.Layout> {
  const _timeseriesTicks = buildTimeseriesLayoutTicks(layoutOptions);
  const timeseriesTickPatch =
    mode === 'timeseries' && _timeseriesTicks
      ? {
          type: 'date' as const,
          tickmode: 'array' as const,
          tickvals: [..._timeseriesTicks.tickvals],
          ticktext: [..._timeseriesTicks.ticktext],
          hoverformat: TIMESERIES_XAXIS_HOVERFORMAT,
        }
      : {};
  const showRangeSlider = mode === 'timeseries' && prefs.showRangeSlider;
  const layout: Partial<Plotly.Layout> =
    mode === 'scatter'
      ? {
          hoverlabel: {
            align: 'left',
            namelength: 0,
          },
        }
      : mode === 'timeseries'
      ? {
          hovermode: 'x unified',
          hoverlabel: {
            align: 'left',
            namelength: 0,
          },
        }
      : {};

  // -- build the X axis --

  const xAxisKey = findXAxisKey(prefs);
  const xLayoutId = getPlotlyXLayoutId();
  const axisKeyToColor = getAxisKeyToColor(prefs);
  if (xAxisKey) {
    const axis = prefs.axisPrefs[xAxisKey];
    const color = axisKeyToColor[xAxisKey];
    const { range, fixedrange } = resolveAxisPlotlyLayoutFields(axis);
    layout[xLayoutId as 'xaxis'] = {
      title: { text: axis.displayName || xAxisKey, font: { color } },
      tickfont: { color },
      type: mode === 'timeseries' ? ('date' as const) : ('linear' as const),
      ...(mode === 'timeseries'
        ? {
            showspikes: true,
            spikemode: 'across' as const,
            ...timeseriesTickPatch,
          }
        : {}),
      range,
      fixedrange,
    };
  }

  // -- get the visible Y axes in order --

  const axesWithVisibleCurves = new Set<AxisKey>(
    Object.entries(prefs.dataSourceToAxis)
      .filter(([dsKey]) => prefs.curvePrefs[dsKey]?.visible !== false)
      .map(([, axisKey]) => axisKey)
  );

  const yKeys = prefs.yAxisOrder.filter(
    (key) =>
      prefs.axisPrefs[key]?.visible !== false &&
      !prefs.axisPrefs[key]?.isXAxis &&
      axesWithVisibleCurves.has(key)
  );

  // -- early return with X axis only if there are no Y axes --

  if (yKeys.length === 0) {
    layout.xaxis = {
      ...(layout.xaxis ?? {}),
      ...(mode === 'timeseries'
        ? {
            showspikes: true,
            spikemode: 'across' as const,
            ...timeseriesTickPatch,
          }
        : {}),
      ...(showRangeSlider ? { rangeslider: {} } : {}),
    };

    return state
      ? mergeFilteredLayoutState(layout, state, layoutStateWhitelist)
      : layout;
  }

  // -- compute the left/right positions for the Y axes --

  const total = yKeys.length;
  const [leftPos, rightPos] = centermostPositions(total);
  layout.xaxis = {
    ...(layout.xaxis ?? {}),
    domain: [leftPos, rightPos],
    ...(mode === 'timeseries'
      ? {
          showspikes: true,
          spikemode: 'across' as const,
          ...timeseriesTickPatch,
        }
      : {}),
    ...(showRangeSlider ? { rangeslider: {} } : {}),
  };
  const overlayedKey = yKeys.find(
    (_, i) => positionFromIndex(i + 1, total) === 0
  );
  const overlayed = overlayedKey ? getPlotlyYShortId(prefs, overlayedKey) : 'y';

  // -- add the Y axes to the layout, including stateful overrides --

  yKeys.forEach((axisKey, index) => {
    const axis = prefs.axisPrefs[axisKey];
    const pos = positionFromIndex(index + 1, total);
    const color = axisKeyToColor[axisKey];

    const yLayoutId = getPlotlyYLayoutId(prefs, axisKey);
    const { range, fixedrange } = resolveAxisPlotlyLayoutFields(axis);
    layout[yLayoutId as 'yaxis'] = {
      title: { text: axis.displayName || axisKey, font: { color } },
      tickfont: { color },
      side: pos < 0.5 ? 'left' : 'right',
      position: pos,
      range,
      fixedrange,
      ...(pos !== 0 ? { overlaying: overlayed as 'y', anchor: 'free' } : {}),
    };
  });

  // -- return the built layout with the layout state overrides --

  return state
    ? mergeFilteredLayoutState(layout, state, layoutStateWhitelist)
    : layout;
}
